/**
 * Arabic buyer guides, October 2026.
 *
 * Each one targets an Arabic query cluster where the current results are thin,
 * translated or off-topic (keyword research, 13 Oct 2026):
 *   - بالتقسيط / بدون دفعة أولى     → dubai-apartments-installments-no-down-payment-ar
 *   - خطة 1% شهرياً                 → one-percent-monthly-payment-plan-dubai-ar
 *   - هل الشراء على الخارطة آمن      → is-off-plan-safe-dubai-escrow-ar
 *   - الإقامة الذهبية على الخارطة    → golden-visa-off-plan-property-dubai-ar
 *   - رسوم 4% والأوقود              → dubai-property-buying-fees-dld-oqood-ar
 *
 * Rules these follow: the spelling variants people type (الخارطة/الخريطة،
 * تقسيط/أقساط، دفعة أولى/مقدم) appear in headings and FAQ; every rule that can
 * change carries a date and says where to confirm it; nothing promises a
 * price, a yield or a visa outcome; each guide links into the live inventory
 * (area pages) rather than ending in a dead end.
 *
 * The prerendered HTML carries the excerpt and FAQ, not the body, so those two
 * fields are written to stand on their own.
 */

const WA = 'https://wa.me/971521138999';

export const guidesAr = [
    {
        slug: 'dubai-apartments-installments-no-down-payment-ar',
        lang: 'ar',
        title: 'شقق دبي بالتقسيط وبدون دفعة أولى 2026 — ما الممكن فعلاً؟',
        description: 'هل يمكن شراء شقة في دبي بدون دفعة أولى أو بمقدم بسيط؟ خطط التقسيط من المطوّر، كم تدفع فعلاً عند الحجز، وأين تجد أقل دفعة أولى في 2026.',
        keywords: 'شقق للبيع في دبي بالتقسيط, شقق بدون دفعة أولى دبي, شقق دبي بالأقساط, شقة في دبي بمقدم بسيط, تقسيط مريح دبي, عقارات دبي على الخارطة بالتقسيط',
        image: '/logo.png',
        publishedAt: '2026-10-13T08:00:00Z',
        updatedAt: '2026-10-13T08:00:00Z',
        readingTime: 7,
        excerpt: 'نعم، يمكن شراء شقة في دبي بالتقسيط المباشر من المطوّر دون بنك ودون فوائد، لكن "بدون دفعة أولى" بالمعنى الحرفي نادر. في أغلب المشاريع على الخارطة تدفع عند الحجز بين 5% و20% من السعر، ويُضاف إليها 4% رسوم دائرة الأراضي، ثم تُقسَّط بقية الثمن شهرياً أو على مراحل البناء، وأحياناً بعد الاستلام.',
        content: `
<p class="lead">"شقق للبيع في دبي بالتقسيط بدون دفعة أولى" من أكثر العبارات بحثاً عند العرب الراغبين في التملّك في دبي. الجواب الصادق: التقسيط متاح على نطاق واسع ومن المطوّر مباشرة بلا فوائد، أما الدخول بلا أي مبلغ عند الحجز فشبه غير موجود. هذا الدليل يشرح ما تدفعه فعلاً، وكيف تقارن بين خطط السداد.</p>

<h2>هل يوجد فعلاً شقق في دبي بدون دفعة أولى (بدون مقدم)؟</h2>
<p>نادراً جداً. حتى العروض التي تُسوَّق بعبارة "بدون دفعة أولى" تطلب عادةً عند الحجز:</p>
<ul>
  <li><strong>رسوم دائرة الأراضي والأملاك 4%</strong> من سعر الوحدة، وتُدفع عند تسجيل العقد (الأوقود) ولا يقسّطها معظم المطوّرين.</li>
  <li><strong>مبلغ حجز</strong> يتراوح غالباً بين 5% و10%، أو دفعة أولى بين 10% و20% حسب المشروع.</li>
</ul>
<p>لذلك الأدقّ أن تبحث عن <strong>"أقل دفعة أولى"</strong> بدلاً من "بدون دفعة أولى". كثير من المشاريع على الخارطة تبدأ بدفعة 5% أو 10% فقط، وهذا هو المعنى الواقعي لعبارة "مقدم بسيط".</p>

<h2>كيف يعمل التقسيط في المشاريع على الخارطة (على الخريطة)؟</h2>
<p>عندما تشتري على الخارطة، المطوّر نفسه هو من يقسّط لك الثمن — لا بنك ولا فوائد ولا دراسة دخل. تُوزَّع الدفعات بإحدى الطرق الآتية:</p>
<table>
<thead><tr><th>الخطة</th><th>كيف تُدفع</th><th>تناسب من؟</th></tr></thead>
<tbody>
<tr><td>حسب مراحل البناء (مثل 60/40 أو 50/50)</td><td>دفعات مرتبطة بنسبة الإنجاز، والباقي عند الاستلام</td><td>من يملك سيولة على فترات</td></tr>
<tr><td>شهري (1% شهرياً مثلاً)</td><td>قسط ثابت كل شهر حتى الاستلام</td><td>أصحاب الدخل الشهري الثابت</td></tr>
<tr><td>بعد الاستلام (Post-Handover)</td><td>جزء من الثمن يُقسَّط سنتين إلى خمس بعد استلام الوحدة</td><td>من يريد دفع الأقساط من الإيجار</td></tr>
<tr><td>20/80</td><td>20% خلال البناء و80% عند الاستلام (نقداً أو بتمويل بنكي)</td><td>من يخطّط لتمويل عقاري عند الاستلام</td></tr>
</tbody>
</table>

<h2>مثال عملي: شقة بسعر 700 ألف درهم</h2>
<ul>
  <li>رسوم دائرة الأراضي 4%: <strong>28,000 درهم</strong> (عند الحجز).</li>
  <li>دفعة أولى 10%: <strong>70,000 درهم</strong>.</li>
  <li>إذا كانت الخطة 1% شهرياً: قسط شهري قدره <strong>7,000 درهم</strong> حتى الاستلام.</li>
</ul>
<p>أي أن ما تحتاجه عند الحجز في هذا المثال نحو 98 ألف درهم، لا "صفر". الأرقام توضيحية، والخطة الفعلية تختلف من مشروع لآخر.</p>

<h2>أين تجد أقل دفعة أولى؟</h2>
<p>تتركّز أقل أسعار الدخول وأطول خطط التقسيط في المناطق متوسطة السعر، مثل:</p>
<ul>
  <li><a href="/area/jvc">مشاريع JVC على الخارطة</a></li>
  <li><a href="/area/dubailand-residence-complex">مشاريع مجمع دبي لاند السكني</a></li>
  <li><a href="/area/dubai-south">مشاريع دبي الجنوب</a></li>
  <li><a href="/area/arjan">مشاريع أرجان</a> و<a href="/area/majan">مجان</a></li>
</ul>
<p>كل صفحة منطقة تعرض المشاريع المتاحة الآن مرتّبة من الأرخص، بأسعار المطوّر مباشرة.</p>

<h2>نصائح قبل أن توقّع</h2>
<ul>
  <li>اطلب <strong>جدول السداد مكتوباً</strong> بالتواريخ والمبالغ، وتأكّد أن رسوم الـ 4% محسوبة.</li>
  <li>تأكّد أن الدفعات تُحوَّل إلى <strong>حساب الضمان (Escrow)</strong> الخاص بالمشروع، لا إلى حساب المطوّر العام. راجع دليلنا: <a href="/blog/is-off-plan-safe-dubai-escrow-ar">هل الشراء على الخارطة آمن؟</a></li>
  <li>اسأل عن <strong>رسوم الخدمة السنوية</strong> بعد الاستلام، فهي جزء من تكلفتك الحقيقية.</li>
  <li>إذا كانت الخطة تتضمّن دفعة كبيرة عند الاستلام، خطّط لمصدرها مبكراً: نقداً، أو تمويلاً بنكياً، أو بيع الوحدة.</li>
</ul>

<h2>كيف نساعدك؟</h2>
<p>نبيع بسعر المطوّر الرسمي دون أي عمولة على المشتري، ونقارن لك خطط السداد في المشاريع التي تناسب ميزانيتك. <a href="${WA}">أرسل لنا ميزانيتك على واتساب</a> ونرسل لك أقل الدفعات الأولى المتاحة هذا الأسبوع.</p>
        `,
        faq: [
            {
                q: 'هل يمكن شراء شقة في دبي بدون دفعة أولى؟',
                a: 'نادراً. حتى العروض المسوّقة بلا دفعة أولى تطلب عادةً رسوم دائرة الأراضي 4% ومبلغ حجز. الأقرب للواقع مشاريع بدفعة أولى 5% إلى 10% ثم أقساط من المطوّر بلا فوائد.'
            },
            {
                q: 'هل التقسيط من المطوّر في دبي بفوائد؟',
                a: 'لا في الغالب. خطط السداد في المشاريع على الخارطة يقدّمها المطوّر مباشرة بلا فوائد وبلا بنك، وتُوزَّع على فترة البناء وأحياناً بعد الاستلام.'
            },
            {
                q: 'كم أدفع عند حجز شقة على الخارطة في دبي؟',
                a: 'عادةً رسوم دائرة الأراضي 4% من السعر، مضافاً إليها دفعة أولى بين 5% و20% حسب المشروع. لشقة بسعر 700 ألف درهم ودفعة 10% يكون المجموع نحو 98 ألف درهم.'
            },
            {
                q: 'هل يمكن للأجنبي شراء شقة بالتقسيط في دبي؟',
                a: 'نعم، يحقّ لجميع الجنسيات التملّك في مناطق التملّك الحر، وخطط التقسيط من المطوّر لا تشترط الإقامة في الإمارات.'
            },
            {
                q: 'ما الفرق بين التقسيط من المطوّر والتمويل البنكي؟',
                a: 'التقسيط من المطوّر بلا فوائد ولا دراسة دخل، لكنه ينتهي عادةً عند الاستلام أو بعده بسنوات قليلة. التمويل البنكي أطول مدة لكنه بفوائد ويشترط دخلاً ودفعة أولى أعلى.'
            }
        ]
    },

    {
        slug: 'one-percent-monthly-payment-plan-dubai-ar',
        lang: 'ar',
        title: 'خطة سداد 1% شهرياً في دبي — كيف تعمل وكم القسط فعلاً؟',
        description: 'شرح خطة 1% شهرياً للعقارات على الخارطة في دبي: الدفعة الأولى، القسط الشهري، الدفعة عند الاستلام، مع أمثلة بالأرقام ومشاريع متاحة بها في 2026.',
        keywords: 'خطة 1% شهريا دبي, نظام 1% دبي, ادفع 1% شهريا, قسط شهري 1%, 1% payment plan dubai, عقارات دبي بالتقسيط الشهري',
        image: '/logo.png',
        publishedAt: '2026-10-13T08:00:00Z',
        updatedAt: '2026-10-13T08:00:00Z',
        readingTime: 6,
        excerpt: 'خطة 1% شهرياً تعني أن تدفع كل شهر 1% من سعر العقار بعد الدفعة الأولى، حتى الاستلام أو بعده. لشقة بسعر 600 ألف درهم يكون القسط 6,000 درهم شهرياً. تبدأ هذه الخطط عادةً بدفعة أولى بين 10% و20% إضافة إلى 4% رسوم دائرة الأراضي، وقد تنتهي بدفعة أكبر عند الاستلام.',
        content: `
<p class="lead">انتشرت خطط "1% شهرياً" في دبي لأنها تحوّل شراء العقار إلى قسط يشبه الإيجار. لكن تفاصيلها تختلف كثيراً بين مشروع وآخر، والفرق يظهر في الدفعة الأولى وفي ما يتبقّى عليك عند الاستلام.</p>

<h2>ما هي خطة 1% شهرياً؟</h2>
<p>بعد دفعة أولى (غالباً 10% إلى 20%)، تدفع كل شهر قسطاً يساوي 1% من سعر الوحدة. يحدّد عدد الأشهر حتى الاستلام ما إذا كانت الأقساط تغطّي كامل الثمن أم يبقى جزء يُدفع عند الاستلام أو بعده.</p>

<h2>مثال بالأرقام</h2>
<table>
<thead><tr><th>البند</th><th>شقة 600 ألف درهم</th><th>تاون هاوس 1.5 مليون درهم</th></tr></thead>
<tbody>
<tr><td>رسوم دائرة الأراضي 4%</td><td>24,000</td><td>60,000</td></tr>
<tr><td>دفعة أولى 10%</td><td>60,000</td><td>150,000</td></tr>
<tr><td>القسط الشهري 1%</td><td>6,000</td><td>15,000</td></tr>
<tr><td>بعد 36 شهراً من الأقساط</td><td>تكون دفعت 46% من الثمن</td><td>تكون دفعت 46% من الثمن</td></tr>
<tr><td>المتبقي عند الاستلام (إن وُجد)</td><td>54%</td><td>54%</td></tr>
</tbody>
</table>
<p>المثال يوضح أهم نقطة: <strong>إذا كان الاستلام بعد 3 سنوات، فالأقساط الشهرية لا تكفي وحدها</strong>، ويبقى أكثر من نصف الثمن عند الاستلام إلا إذا كانت الخطة تستمر بعد الاستلام (Post-Handover). اقرأ الجدول كاملاً لا القسط الشهري فقط.</p>

<h2>متى تكون خطة 1% مناسبة لك؟</h2>
<ul>
  <li>إذا كان لديك <strong>دخل شهري ثابت</strong> أعلى من القسط براحة.</li>
  <li>إذا كانت الخطة <strong>تستمر بعد الاستلام</strong>، فيمكن أن يغطّي الإيجار جزءاً من القسط.</li>
  <li>إذا كنت تخطّط لـ <strong>بيع الوحدة قبل الاستلام</strong>، تذكّر أن المطوّر يشترط عادةً سداد نسبة معيّنة (غالباً 30% إلى 40%) قبل السماح بإعادة البيع.</li>
</ul>

<h2>عروض حالية بخطة 1%</h2>
<p><strong>Greens Townhouses</strong> قرب Dubai International Academic City: تاون هاوس مفروش بخطة 1% شهرياً حتى الاستلام، مع خطة دفع بعد الاستلام، وعرض حصري للأطبّاء بنسبة 0.5% شهرياً وفق شروط المطوّر. <a href="${WA}">اطلب التفاصيل على واتساب</a>.</p>
<p>ولتصفّح مشاريع أخرى بخطط شهرية: <a href="/area/jvc">JVC</a>، <a href="/area/dubai-south">دبي الجنوب</a>، <a href="/area/arjan">أرجان</a>، <a href="/area/damac-islands">داماك آيلاندز</a>.</p>

<h2>أسئلة تطرحها قبل الحجز</h2>
<ul>
  <li>كم الدفعة الأولى بالضبط؟ وهل رسوم الـ 4% منفصلة؟</li>
  <li>كم شهراً ستستمر الأقساط؟ وكم يتبقّى عند الاستلام؟</li>
  <li>هل يوجد جزء بعد الاستلام؟ وعلى كم سنة؟</li>
  <li>ما نسبة السداد المطلوبة لإعادة البيع قبل الاستلام؟</li>
</ul>
<p>للمقارنة بخطط أخرى راجع: <a href="/blog/dubai-apartments-installments-no-down-payment-ar">شقق دبي بالتقسيط وبدون دفعة أولى</a>.</p>
        `,
        faq: [
            {
                q: 'كم القسط الشهري في خطة 1% لشقة بسعر 600 ألف درهم؟',
                a: 'القسط 1% من السعر، أي 6,000 درهم شهرياً، بعد دفعة أولى غالباً بين 10% و20% و4% رسوم دائرة الأراضي.'
            },
            {
                q: 'هل خطة 1% شهرياً تغطي كامل سعر العقار؟',
                a: 'ليس دائماً. إذا كان الاستلام بعد 36 شهراً فالأقساط تغطّي 36% فقط، ويبقى الباقي عند الاستلام إلا إذا كانت الخطة تستمر بعد الاستلام.'
            },
            {
                q: 'هل خطة 1% بفوائد؟',
                a: 'لا، هي خطة سداد من المطوّر بلا فوائد، والسعر نفسه هو سعر المطوّر الرسمي.'
            },
            {
                q: 'هل يوجد عرض 0.5% شهرياً للأطباء في دبي؟',
                a: 'نعم، يقدّم مشروع Greens Townhouses قرب Dubai International Academic City خطة 0.5% شهرياً للأطبّاء حتى الاستلام وفق شروط المطوّر وإثبات المهنة، مقابل 1% شهرياً للعموم.'
            }
        ]
    },

    {
        slug: 'is-off-plan-safe-dubai-escrow-ar',
        lang: 'ar',
        title: 'هل الشراء على الخارطة في دبي آمن؟ حساب الضمان وحماية أموالك',
        description: 'كيف يحمي القانون أموالك عند شراء عقار على الخارطة في دبي: حساب الضمان (Escrow) وفق القانون 8 لسنة 2007، تسجيل الأوقود، وماذا يحدث إذا تأخّر المطوّر.',
        keywords: 'هل الشراء على الخارطة آمن, حساب الضمان دبي, escrow دبي, مخاطر الشراء على الخارطة, ضمان أموال المشتري دبي, تأخر المطور في التسليم',
        image: '/logo.png',
        publishedAt: '2026-10-13T08:00:00Z',
        updatedAt: '2026-10-13T08:00:00Z',
        readingTime: 7,
        excerpt: 'الشراء على الخارطة في دبي محمي بالقانون رقم 8 لسنة 2007، الذي يلزم كل مطوّر بفتح حساب ضمان (Escrow) خاص بكل مشروع لدى بنك معتمد قبل بيع أي وحدة. تُودَع دفعاتك في هذا الحساب ولا تُصرف للمطوّر إلا بحسب تقدّم البناء. الخطر الحقيقي يبدأ عندما تدفع خارج هذا الحساب أو لمشروع غير مسجّل.',
        content: `
<p class="lead">أكثر سؤال يطرحه المشتري العربي قبل الحجز: ماذا لو لم يُبنَ المشروع؟ في دبي هذا السؤال له جواب قانوني واضح منذ 2007، والمهم أن تعرف كيف تتأكّد أن حمايتك مفعّلة فعلاً.</p>

<h2>ما هو حساب الضمان (Escrow)؟</h2>
<p>بموجب <strong>القانون رقم 8 لسنة 2007</strong> بشأن حسابات ضمان التطوير العقاري في إمارة دبي، لا يحقّ للمطوّر بيع أي وحدة على الخارطة قبل:</p>
<ul>
  <li>تسجيل المشروع لدى دائرة الأراضي والأملاك ومؤسسة التنظيم العقاري (ريرا).</li>
  <li>فتح <strong>حساب ضمان خاص بالمشروع</strong> لدى بنك معتمد.</li>
</ul>
<p>كل دفعاتك تُودَع في هذا الحساب، ولا يصرف البنك منها للمطوّر إلا بحسب نسب الإنجاز التي يعتمدها مهندس المشروع وتراقبها الدائرة. وقد فرضت دائرة الأراضي غرامات على مطوّرين خالفوا قواعد حسابات الضمان، ما يعني أن الرقابة مطبّقة لا نظرية.</p>

<h2>ما هو الأوقود ولماذا يهمّك؟</h2>
<p><strong>الأوقود (Oqood)</strong> هو تسجيل عقد شرائك في السجل العقاري المبدئي لدى دائرة الأراضي. بعد تسجيله تصبح الوحدة مسجّلة باسمك رسمياً قبل اكتمال البناء، ولا يستطيع المطوّر بيعها لغيرك. عند الاستلام يتحوّل الأوقود إلى سند ملكية.</p>

<h2>ماذا يحدث إذا تأخّر المطوّر أو توقّف المشروع؟</h2>
<ul>
  <li><strong>التأخير</strong>: شائع في السوق، وأغلب العقود تتضمّن فترة سماح. اقرأ بند موعد التسليم والتعويض في عقد البيع (SPA) قبل التوقيع.</li>
  <li><strong>التوقّف أو الإلغاء</strong>: لمؤسسة التنظيم العقاري صلاحية التدخّل في المشاريع المتعثّرة، بما في ذلك إلغاؤها وتصفيتها وإعادة الأموال من حساب الضمان للمشترين وفق القانون.</li>
</ul>
<p>هذه الحماية تعمل فقط إذا كانت أموالك داخل حساب الضمان وكان عقدك مسجّلاً. لذلك الخطوات التالية أهم من أي شيء آخر.</p>

<h2>قائمة تحقّق قبل أن تدفع درهماً واحداً</h2>
<ol>
  <li>تأكّد أن المشروع <strong>مسجّل لدى دائرة الأراضي</strong> (عبر تطبيق Dubai REST أو موقع الدائرة).</li>
  <li>اطلب <strong>رقم حساب الضمان واسم البنك</strong>، وحوّل الدفعات إليه مباشرة باسم المشروع.</li>
  <li>لا تدفع نقداً ولا إلى حساب شخصي ولا إلى حساب الوسيط.</li>
  <li>تأكّد من تسجيل <strong>الأوقود</strong> باسمك بعد الحجز، واحتفظ بنسخة منه.</li>
  <li>تعامل مع <strong>وسيط مرخّص</strong> واطلب رقم ترخيصه؛ رقمنا لدى دائرة الأراضي والأملاك: 96129.</li>
</ol>

<h2>هل يعني ذلك أنه بلا مخاطر؟</h2>
<p>لا. القانون يحمي أموالك من الضياع، لكنه لا يضمن ارتفاع السعر ولا الالتزام الدقيق بموعد التسليم ولا مستوى الإيجار. اختيار المطوّر والمنطقة يبقى قرارك الأهم. يمكنك مقارنة المشاريع حسب المنطقة والمطوّر في <a href="/areas">صفحات المناطق</a>.</p>
<p>هل تريد أن نتحقّق من مشروع بعينه قبل حجزه؟ <a href="${WA}">أرسل لنا اسمه على واتساب</a>.</p>
        `,
        faq: [
            {
                q: 'هل الشراء على الخارطة في دبي آمن؟',
                a: 'نعم إلى حدّ كبير إذا دفعت داخل حساب الضمان. القانون رقم 8 لسنة 2007 يلزم المطوّر بفتح حساب ضمان لكل مشروع لدى بنك معتمد، ولا تُصرف الأموال إلا حسب تقدّم البناء.'
            },
            {
                q: 'ما هو حساب الضمان Escrow في دبي؟',
                a: 'حساب بنكي خاص بكل مشروع على الخارطة تُودَع فيه دفعات المشترين، ولا يصرف البنك منه للمطوّر إلا بحسب نسب الإنجاز المعتمدة تحت رقابة دائرة الأراضي والأملاك.'
            },
            {
                q: 'ماذا يحدث إذا لم يكمل المطوّر المشروع؟',
                a: 'لمؤسسة التنظيم العقاري صلاحية إلغاء المشاريع المتعثّرة وتصفيتها وإعادة الأموال للمشترين من حساب الضمان وفق القانون، بشرط أن تكون الدفعات أُودعت فيه.'
            },
            {
                q: 'كيف أتأكّد أن المشروع مسجّل؟',
                a: 'ابحث عن المشروع في تطبيق Dubai REST أو خدمات دائرة الأراضي والأملاك، واطلب من المطوّر رقم حساب الضمان واسم البنك قبل أي تحويل.'
            },
            {
                q: 'ما هو الأوقود؟',
                a: 'تسجيل عقد شراء الوحدة على الخارطة في السجل العقاري المبدئي لدى دائرة الأراضي. يثبت حقّك في الوحدة قبل اكتمال البناء ويتحوّل إلى سند ملكية عند الاستلام.'
            }
        ]
    },

    {
        slug: 'golden-visa-off-plan-property-dubai-ar',
        lang: 'ar',
        title: 'الإقامة الذهبية بعقار في دبي 2026 — هل تكفي الخارطة؟',
        description: 'شروط الإقامة الذهبية لعشر سنوات عبر العقار في دبي: حدّ 2 مليون درهم، العقارات على الخارطة، العقار بالتقسيط أو بالرهن، والأوراق المطلوبة. محدّث أكتوبر 2026.',
        keywords: 'الإقامة الذهبية عقار دبي, الإقامة الذهبية على الخارطة, الفيزا الذهبية 2 مليون, التأشيرة الذهبية دبي شراء عقار, golden visa property dubai',
        image: '/logo.png',
        publishedAt: '2026-10-13T08:00:00Z',
        updatedAt: '2026-10-13T08:00:00Z',
        readingTime: 6,
        excerpt: 'تُمنح الإقامة الذهبية لعشر سنوات قابلة للتجديد لمن يملك عقاراً في دبي بقيمة 2 مليون درهم أو أكثر، ويشمل ذلك العقارات على الخارطة من مطوّرين معتمدين. تغيّرت شروط المبلغ المدفوع فعلاً للعقارات على الخارطة والمرهونة أكثر من مرة، لذلك تأكّد من الشروط السارية لدى دائرة الأراضي والأملاك قبل التقديم. (المعلومات محدّثة حتى أكتوبر 2026.)',
        content: `
<p class="lead">الإقامة الذهبية من أقوى أسباب شراء العقار في دبي اليوم: إقامة لعشر سنوات لك ولعائلتك دون كفيل. لكن المعلومات المتداولة عنها متضاربة، خصوصاً للعقارات على الخارطة. هذا ما هو ثابت، وما يجب التحقّق منه.</p>

<h2>الشرط الأساسي: عقار بقيمة 2 مليون درهم</h2>
<ul>
  <li>قيمة العقار (أو مجموع عقاراتك) <strong>2 مليون درهم أو أكثر</strong>.</li>
  <li>مدة الإقامة <strong>10 سنوات قابلة للتجديد</strong> ما دمت تملك العقار.</li>
  <li>يمكن أن تشمل الزوج/الزوجة والأبناء.</li>
</ul>

<h2>هل يكفي عقار على الخارطة (على الخريطة)؟</h2>
<p>نعم، العقارات على الخارطة من مطوّرين معتمدين لدى دائرة الأراضي مؤهّلة للإقامة الذهبية. النقطة التي تغيّرت عبر السنوات هي <strong>كم يجب أن تكون قد دفعت فعلاً</strong> من الثمن قبل التقديم، وكيف تُعامَل العقارات المرهونة لبنك. تشير تقارير السوق إلى تعديل في فبراير 2026 جعل تقييم دائرة الأراضي للعقار بمليوني درهم هو المعيار الأساسي، لكننا لم نجد إعلاناً رسمياً منشوراً بذلك، لذلك:</p>
<p><strong>قبل الشراء بغرض الإقامة الذهبية، اطلب منّا أو من مركز خدمة دائرة الأراضي تأكيد الشروط السارية في يوم التقديم.</strong></p>

<h2>العقار بالتقسيط أو بالرهن</h2>
<ul>
  <li>العقار المموّل من بنك يحتاج عادةً <strong>شهادة عدم ممانعة من البنك</strong>.</li>
  <li>العقار المشترى بخطة سداد من المطوّر يُنظر فيه إلى قيمة العقار وما سُدّد منه وفق الشروط السارية.</li>
</ul>

<h2>كم مشروعاً يؤهّل للإقامة الذهبية؟</h2>
<p>كل <a href="/areas">صفحة منطقة</a> لدينا تعرض عدد المشاريع التي يبدأ سعرها من مليوني درهم فأكثر. تتركّز هذه المشاريع في <a href="/area/palm-jumeirah">نخلة جميرا</a>، <a href="/area/downtown-dubai">داون تاون</a>، <a href="/area/dubai-hills">دبي هيلز</a>، <a href="/area/business-bay">بزنس باي</a> و<a href="/area/dubai-creek-harbour">دبي كريك هاربر</a>، كما يمكن الوصول إلى الحدّ بوحدة أكبر في مناطق أقل سعراً أو بأكثر من وحدة.</p>

<h2>الأوراق المطلوبة عادةً</h2>
<ul>
  <li>جواز السفر وصورة شخصية.</li>
  <li>سند الملكية أو الأوقود، مع تقييم العقار.</li>
  <li>شهادة عدم ممانعة من البنك إذا كان العقار مرهوناً.</li>
  <li>تأمين صحي ساري.</li>
</ul>
<p>تُقدَّم الطلبات عبر مراكز دائرة الأراضي والأملاك والإدارة العامة للإقامة وشؤون الأجانب في دبي.</p>

<h2>نصيحتنا</h2>
<p>لا تشترِ عقاراً بمليوني درهم من أجل الإقامة فقط؛ اختره لأنه استثمار جيد أولاً، والإقامة ميزة إضافية. <a href="${WA}">تواصل معنا على واتساب</a> وأخبرنا بميزانيتك، ونرشّح لك مشاريع مؤهّلة ونتحقّق معك من الشروط السارية.</p>
        `,
        faq: [
            {
                q: 'كم سعر العقار المطلوب للإقامة الذهبية في دبي؟',
                a: 'مليونا درهم إماراتي أو أكثر، في عقار واحد أو مجموع عقارات. الإقامة لعشر سنوات قابلة للتجديد.'
            },
            {
                q: 'هل تُمنح الإقامة الذهبية لعقار على الخارطة؟',
                a: 'نعم، العقارات على الخارطة من مطوّرين معتمدين مؤهّلة. شروط المبلغ المدفوع فعلاً تغيّرت أكثر من مرة، فتأكّد من الشروط السارية لدى دائرة الأراضي قبل التقديم.'
            },
            {
                q: 'هل يمكن الحصول على الإقامة الذهبية بعقار مرهون؟',
                a: 'نعم في الغالب، مع شهادة عدم ممانعة من البنك الممول ووفق الشروط السارية وقت التقديم.'
            },
            {
                q: 'هل تشمل الإقامة الذهبية العائلة؟',
                a: 'نعم، يمكن كفالة الزوج أو الزوجة والأبناء ضمن الإقامة الذهبية.'
            }
        ]
    },

    {
        slug: 'dubai-property-buying-fees-dld-oqood-ar',
        lang: 'ar',
        title: 'رسوم شراء عقار في دبي 2026 — 4% دائرة الأراضي والأوقود',
        description: 'كل تكاليف شراء عقار في دبي بالأرقام: رسوم دائرة الأراضي 4%، تسجيل الأوقود للعقار على الخارطة، رسوم أمين التسجيل، الرهن، ورسوم الخدمة السنوية.',
        keywords: 'رسوم دائرة الأراضي 4%, رسوم الأوقود, تكاليف شراء عقار في دبي, رسوم تسجيل عقار دبي, رسوم الخدمة السنوية دبي, dld fees',
        image: '/logo.png',
        publishedAt: '2026-10-13T08:00:00Z',
        updatedAt: '2026-10-13T08:00:00Z',
        readingTime: 6,
        excerpt: 'أكبر تكلفة إضافية عند شراء عقار في دبي هي رسوم دائرة الأراضي والأملاك: 4% من سعر العقار، ويدفعها المشتري عادةً كاملة في المشاريع على الخارطة عند تسجيل الأوقود، مع رسوم إدارية بسيطة. لا تُدفع 4% مرة ثانية عند الاستلام. وعند الشراء عبرنا لا توجد عمولة وسيط على المشتري. (محدّث أكتوبر 2026.)',
        content: `
<p class="lead">سعر الوحدة ليس كل ما ستدفعه. قبل أن تحسب ميزانيتك، هذه التكاليف الإضافية عند شراء عقار في دبي، على الخارطة وجاهز.</p>

<h2>رسوم دائرة الأراضي والأملاك 4%</h2>
<p>تُفرض رسوم تسجيل نسبتها <strong>4% من سعر العقار</strong>. في المشاريع على الخارطة (البيع الأوّل من المطوّر) <strong>يدفعها المشتري عادةً كاملة</strong> عند تسجيل العقد، ويُضاف إليها مبلغ إداري صغير. بعض المطوّرين يقدّمون عروضاً يتحمّلون فيها جزءاً من هذه الرسوم أو كلها.</p>
<p>مثال: شقة بسعر 1,000,000 درهم ← رسوم 40,000 درهم.</p>

<h2>تسجيل الأوقود (للعقار على الخارطة)</h2>
<p>الأوقود هو تسجيل عقد الشراء في السجل العقاري المبدئي. رسوم الـ 4% تُدفع عند هذا التسجيل، وعند الاستلام يتحوّل الأوقود إلى سند ملكية <strong>دون دفع 4% مرة أخرى</strong>. اقرأ أكثر في <a href="/blog/is-off-plan-safe-dubai-escrow-ar">دليل حماية أموال المشتري</a>.</p>

<h2>تكاليف العقار الجاهز (إعادة البيع)</h2>
<table>
<thead><tr><th>البند</th><th>التكلفة التقريبية</th></tr></thead>
<tbody>
<tr><td>رسوم دائرة الأراضي</td><td>4% من السعر + رسوم إصدار السند</td></tr>
<tr><td>رسوم مكتب أمين التسجيل</td><td>نحو 2,000 درهم للعقار الأقل من 500 ألف، ونحو 4,000 درهم لما فوقه، مع ضريبة القيمة المضافة</td></tr>
<tr><td>تسجيل الرهن (إن وُجد تمويل)</td><td>0.25% من قيمة القرض + رسوم إدارية</td></tr>
<tr><td>عمولة الوسيط</td><td>غالباً 2% في السوق الثانوي</td></tr>
</tbody>
</table>
<p>الأرقام تقريبية وقد تتغيّر؛ تُحسب القيمة الدقيقة عند التسجيل.</p>

<h2>رسوم الخدمة السنوية</h2>
<p>بعد الاستلام يدفع المالك <strong>رسوم خدمة سنوية</strong> تُحسب بالقدم المربّع، وتختلف حسب المبنى والمنطقة ومستوى المرافق. تُنشر الأرقام الرسمية لكل مبنى في مؤشّر رسوم الخدمة لدى مؤسسة التنظيم العقاري. احسبها ضمن العائد الإيجاري الصافي قبل الشراء.</p>

<h2>عمولة الوسيط في المشاريع على الخارطة</h2>
<p>في البيع الأوّل من المطوّر، يدفع المطوّر عمولة الوسيط عادةً، لا المشتري. عندنا القاعدة ثابتة: <strong>تشتري بسعر المطوّر الرسمي ولا تدفع لنا أي عمولة</strong>.</p>

<h2>ملخّص تكلفة شراء شقة على الخارطة بسعر 800 ألف درهم</h2>
<ul>
  <li>سعر الوحدة: 800,000 درهم (يُقسَّط حسب خطة المطوّر).</li>
  <li>رسوم دائرة الأراضي 4%: 32,000 درهم عند التسجيل.</li>
  <li>رسوم إدارية: مبلغ بسيط عند التسجيل.</li>
  <li>عمولة وسيط: صفر عبر الزعيم العيناوي.</li>
</ul>
<p>تصفّح المشاريع حسب الميزانية في <a href="/areas">صفحات المناطق</a>، أو <a href="${WA}">اطلب حساب التكلفة الكاملة لمشروع محدّد على واتساب</a>.</p>
        `,
        faq: [
            {
                q: 'كم رسوم دائرة الأراضي عند شراء عقار في دبي؟',
                a: '4% من سعر العقار، إضافة إلى رسوم إدارية بسيطة. في المشاريع على الخارطة يدفعها المشتري عادةً كاملة عند تسجيل الأوقود.'
            },
            {
                q: 'من يدفع رسوم الـ 4% في العقار على الخارطة؟',
                a: 'المشتري عادةً، عند تسجيل العقد لدى دائرة الأراضي، إلا إذا كان لدى المطوّر عرض يتحمّل فيه الرسوم كلها أو جزءاً منها.'
            },
            {
                q: 'هل أدفع 4% مرة أخرى عند استلام العقار على الخارطة؟',
                a: 'لا. عند الاستلام يتحوّل الأوقود إلى سند ملكية دون إعادة دفع رسوم الـ 4%.'
            },
            {
                q: 'هل أدفع عمولة وسيط عند شراء عقار على الخارطة؟',
                a: 'في البيع الأوّل من المطوّر يدفع المطوّر عمولة الوسيط عادةً. عند الشراء عبر الزعيم العيناوي لا يدفع المشتري أي عمولة.'
            }
        ]
    }
];
